import { PoiType } from '@/enums/floor-guide';
import { useCurrentBuilding } from '@/hooks/useCurrentBuilding';
import { useConfigStore } from '@/stores';
import { useCallback, useMemo } from 'react';

export const usePoiDetail = () => {
    const { currentBuilding } = useCurrentBuilding();
    const allBuildings = useConfigStore((state) => state?.config?.server?.store?.buildings);

    const allPoiContents = useMemo(() => {
        if (!Array.isArray(allBuildings) || !allBuildings?.length) {
            return [];
        }

        return allBuildings.flatMap((building) => (Array.isArray(building.poiContents) ? building.poiContents : []));
    }, [allBuildings]);

    const facilityPoiList = useMemo(() => {
        const facility = allBuildings?.flatMap((item) => item?.filterPoiContents.facility);
        const otherFacility = allBuildings?.flatMap((item) => item?.filterPoiContents.otherUtility);

        return [...(Array.isArray(facility) ? facility : []), ...(Array.isArray(otherFacility) ? otherFacility : [])];
    }, [allBuildings]);

    const handleGetFacilityPoi = useCallback(
        (studioPoiId: string) => {
            if (!studioPoiId || !Array.isArray(facilityPoiList)) {
                return undefined;
            }

            return facilityPoiList.find((poi) => poi.studioPoiId === studioPoiId);
        },
        [facilityPoiList]
    );

    const handleGetShopPoi = useCallback(
        (studioPoiId: string) => {
            const shopPoiList = allBuildings?.flatMap((item) => item?.filterPoiContents.shop);

            if (!Array.isArray(shopPoiList)) {
                return [];
            }

            return shopPoiList.filter((poi) => poi.studioPoiId === studioPoiId);
        },
        [allBuildings]
    );

    const handleGetPoiListInAllBuildings = useCallback(
        (studioPoiId?: string) => {
            if (!Array.isArray(allPoiContents) || !allPoiContents?.length || !studioPoiId) {
                return [];
            }

            return allPoiContents.filter((poi) => poi.studioPoiId === studioPoiId);
        },
        [allPoiContents]
    );

    const fnbCategoryCode: string[] = useMemo(() => {
        if (!Array.isArray(currentBuilding?.filterCategories.fnb)) {
            return [];
        }

        return currentBuilding.filterCategories.fnb.map((item) => item.categoryCode);
    }, [currentBuilding?.filterCategories.fnb]);

    const handleGetPoiItemInAllBuildings = useCallback(
        (studioPoiId?: string) => {
            if (!Array.isArray(allPoiContents) || !allPoiContents?.length || !studioPoiId) {
                return undefined;
            }

            return allPoiContents.find((poi) => poi.studioPoiId === studioPoiId);
        },
        [allPoiContents]
    );

    /**
     * Determine the POI type for a given `studioPoiId`.
     *
     * Classification logic:
     * 1) If a POI with the provided `studioPoiId` exists in `facilityPoiList`, it is classified as FACILITY.
     * 2) Otherwise, gather all POIs in the general `poiContents` that share the same `studioPoiId`.
     *    - If there are more than one, classify as CONTENT.
     *    - If there is exactly one, inspect that POI's `categories`:
     *        - If any category matches an entry in `fnbCategoryCode`, classify as FNB (Food & Beverage).
     *        - Otherwise, classify as SHOP.
     * 3) If none of the above conditions are met, classify as NO_CONTENT.
     *
     * @param studioPoiId - The identifier shared across related POIs (e.g., variants) used to group and classify.
     * @returns The resolved {@link PoiType} for the provided `studioPoiId` (FACILITY | CONTENT | FNB | SHOP | NO_CONTENT).
     */
    const handleCheckPoiType = useCallback(
        (studioPoiId: string): PoiType => {
            const facilityPoi = handleGetFacilityPoi(studioPoiId);

            if (facilityPoi) {
                return PoiType.FACILITY;
            }

            const poiList = handleGetPoiListInAllBuildings(studioPoiId);

            if (Array.isArray(poiList) && poiList?.length > 1) {
                return PoiType.CONTENT;
            }

            if (Array.isArray(poiList) && poiList.length === 1) {
                const categoryCode = poiList[0].categories;

                const isFnb = fnbCategoryCode.some((code) => Array.isArray(categoryCode) && categoryCode.includes(code));

                if (isFnb) {
                    return PoiType.FNB;
                }

                return PoiType.SHOP;
            }

            return PoiType.NO_CONTENT;
        },
        [handleGetFacilityPoi, handleGetPoiListInAllBuildings, fnbCategoryCode]
    );

    return {
        facilityPoiList,
        handleCheckPoiType,
        handleGetFacilityPoi,
        handleGetShopPoi,
        handleGetPoiListInAllBuildings,
        handleGetPoiItemInAllBuildings,
    };
};
